/**
 * Groups and group membership.
 *
 * Replaces the `Groups` keyon envelope: `GetGroupsByAccount`, `GetGroupsByOwner`,
 * `CreateGroup`, `UpdateGroup`, `DeleteGroup`, `GetGroupMembers`,
 * `AddUserToGroup` and `RemoveUserFromGroup`.
 *
 * A group is not a label. `group_permissions` is one of the allow sources in a
 * user's effective permission set, so adding a member can widen what they may
 * read — which is why every write here is `groups:admin` and audited, while the
 * reads are `users:read`. The legacy `SearchGroups` returned every group in the
 * tenant under a read that required no privilege at all.
 */

import api from './client';

// --- groups ----------------------------------------------------------------

export function listGroups(params = {}) {
  return api.list('/groups', params);
}

export function getGroup(groupId, params = {}) {
  return api.get(`/groups/${groupId}`, params);
}

export function createGroup(payload) {
  return api.post('/groups', payload);
}

export function updateGroup(groupId, patch) {
  return api.patch(`/groups/${groupId}`, patch);
}

export function deleteGroup(groupId) {
  return api.delete(`/groups/${groupId}`);
}

// --- membership, from the group --------------------------------------------

export function listGroupMembers(groupId, params = {}) {
  return api.list(`/groups/${groupId}/members`, params);
}

export function addGroupMember(groupId, userId) {
  return api.post(`/groups/${groupId}/members`, { user_id: userId });
}

export function removeGroupMember(groupId, userId) {
  return api.delete(`/groups/${groupId}/members/${userId}`);
}

// --- membership, from the user ---------------------------------------------
//
// The same relation addressed from the other end, because the Edit User screen
// shows one person's groups and the Groups screen shows one group's people.

export function listUserGroups(userId) {
  return api.get(`/users/${userId}/groups`);
}

/** Replace every group the user belongs to — unticking one removes them. */
export function replaceUserGroups(userId, groupIds) {
  return api.put(`/users/${userId}/groups`, { group_ids: groupIds });
}

/**
 * Add and remove in one request. Kept apart from `replaceUserGroups` because a
 * screen that only knows about the checkbox it just changed must not send a
 * complete set it never had, which is how the legacy flow silently dropped
 * memberships it had not loaded.
 */
export function adjustUserGroups(userId, { add = [], remove = [] }) {
  return api.patch(`/users/${userId}/groups`, { add, remove });
}

export default {
  listGroups, getGroup, createGroup, updateGroup, deleteGroup,
  listGroupMembers, addGroupMember, removeGroupMember,
  listUserGroups, replaceUserGroups, adjustUserGroups,
};
